import { range } from 'd3-array';
import { easeCircleInOut, easeExpIn, easePolyInOut, easePolyOut } from 'd3-ease';
import { scaleLinear, scaleTime } from 'd3-scale';
import { select } from 'd3-selection';
import { area, curveCatmullRom, line } from 'd3-shape';
import { timeDay, timeHour, timeMinute } from 'd3-time';
import { transition } from 'd3-transition';

import { defaultTargets } from '../dashboard/TargetContext';
import { consts, degrees, internalUnits } from './consts';
import { glucoseAccessor, timeAccessor } from './dataUtils';

const ticks = Object.values(consts[internalUnits].ticks);
const defaultDomain = Object.freeze([ticks[ticks.length - 1], ticks[0]]);

function defined(d, i, a) {
  if (i === 0 || i === a.length - 1) {
    return true;
  }
  const t0 = new Date(a[i-1].time);
  const t1 = new Date(d.time);
  const t2 = new Date(a[i+1].time);
  if (timeMinute.count(t0, t1) > 20 || timeMinute.count(t1, t2) > 20) {
    return false;
  }
  return true;
}

const emptyTransform = {
  translateX: 0,
  translateY: 0,
  rotate: 0,
  skewX: 0,
  scaleX: 1,
  scaleY: 1
};

function decomposeTransform (a, b, c, d, e, f) {
  let scaleX = Math.sqrt(a * a + b * b);
  const scaleY = Math.sqrt(c * c + d * d);
  let skewX = a * c + b * d;
  if (scaleX) {
    a /= scaleX;
    b /= scaleX;
  }
  if (skewX) {
    c -= a * skewX;
    d -= b * skewX;
  }
  if (scaleY) {
    c /= scaleY;
    d /= scaleY;
    skewX /= scaleY;
  }
  if (a * d < b * c) {
    a = -a;
    b = -b;
    skewX = -skewX;
    scaleX = -scaleX;
  }
  return {
    translateX: e,
    translateY: f,
    rotate: Math.atan2(b, a) * degrees,
    skewX: Math.atan(skewX) * degrees,
    scaleX: scaleX,
    scaleY: scaleY
  };
}

// A lazily initialized variable for transform
let svgNode;

export function parseTransform(value) {
    if (value == null) return emptyTransform;
    if (!svgNode) svgNode = document.createElementNS("http://www.w3.org/2000/svg", "g");
    svgNode.setAttribute("transform", value);
    if (!(value = svgNode.transform.baseVal.consolidate())) return emptyTransform;
    value = value.matrix;
    return decomposeTransform(value.a, value.b, value.c, value.d, value.e, value.f);
} 

export function remove() {
  select(this).remove();
}

export const y = scaleLinear()
  .domain(defaultDomain)
  .range([350, 0]);

export function scaleY(height) {
  return scaleLinear().domain(defaultDomain).range([height, 0]);
};

export function glucoseScale(d) {
  return y(glucoseAccessor(d));
};

export function getGlucoseScale({ height = 350 }) {
  const scaledY = scaleY(height);
  return function glucoseScale(d) {
    return scaledY(glucoseAccessor(d));
  }
};

export function getTimeScaleForDay({ day, width = 720 }) {
  const scale = scaleTime().domain([day, timeDay.offset(day, 1)]).range([0, width]);
  return function timeScale(d) {
    return scale(timeAccessor(d));
  }
};

export function getTimeScaleForDays({ days, width = 720 }) {
  const dayRange = timeDay.range(days[0], timeDay.offset(days[days.length - 1], 2));
  const scale = scaleTime()
    .domain(dayRange)
    .range(range(0, dayRange.length * width, width));
  return function timeScale(d) {
    return scale(timeAccessor(d));
  }
};

export function getHourBasedTimeScale({ width = 720 }) {
  const epoch = timeDay(new Date());
  const endOfDay = timeHour.offset(epoch, 24);
  const scale = scaleTime().domain([epoch, endOfDay]).range([0, width]);
  return function timeScale(d) {
    const dTime = timeAccessor(d);
    const adjustedTime = new Date(epoch.getFullYear(), epoch.getMonth(), epoch.getDate(),
      dTime.getHours(), dTime.getMinutes(), dTime.getSeconds(), dTime.getMilliseconds());
    return scale(adjustedTime);
  }
};

export function getGlucoseLineForDay({ day, width = 720, height = 350, timeScale = getTimeScaleForDay({ day, width }) }) {
  return line()
  .defined(defined)
  .x(timeScale)
  .y(getGlucoseScale({ height }))
  .curve(curveCatmullRom);
};

export function getGlucoseLineForDays({ days, width = 720, height = 350, timeScale = getTimeScaleForDays({ days, width }) }) {
  return line()
  .defined(defined)
  .x(timeScale)
  .y(getGlucoseScale({ height }))
  .curve(curveCatmullRom);
};

export function getHighGlucoseAreaForDay(props) {
  const {
    day,
    width = 720,
    height = 350,
    timeScale = getTimeScaleForDay({ day, width }),
    targets = defaultTargets,
  } = props;
  function definedHigh(d) {
    return glucoseAccessor(d) > targets.top;
  }
  const scaledGlucoseScale = getGlucoseScale({ height });
  return area()
  .defined(definedHigh)
  .x(timeScale)
  .y0(glucoseScale({ glucose: targets.top }))
  .y1(scaledGlucoseScale)
  .curve(curveCatmullRom);
};

export function getLowGlucoseAreaForDay(props) {
  const {
    day,
    width = 720,
    height = 350,
    timeScale = getTimeScaleForDay({ day, width }),
    targets = defaultTargets,
  } = props;
  function definedLow(d, i, a) {
    return (defined(d, i, a) && (glucoseAccessor(d) <= targets.bottom));
  }
  const scaledGlucoseScale = getGlucoseScale({ height });
  return area()
  .defined(definedLow)
  .x(timeScale)
  .y0(glucoseScale({ glucose: targets.bottom }))
  .y1(scaledGlucoseScale)
  .curve(curveCatmullRom);
};

export function fastTransition(selection) {
  return selection.transition()
      .duration(250)
      .ease(easePolyInOut);
};

export function getSlowTransition() {
  return transition()
    .duration(1000)
    .ease(easeCircleInOut);  
};

export function slowTransition(selection) {
  return selection.transition()
      .duration(1000)
      .ease(easeCircleInOut);
};

export function prepareTransition(selection) {
  return selection.transition()
      .duration(250)
      .ease(easeExpIn);
};

export function completeTransition(selection) {
  return selection.transition()
      .duration(750)
      .ease(easePolyOut);
};

export function blurTransition(selection) {
  const t = transition()
      .duration(1000)
      .ease(easeCircleInOut);

  function reset() {
    select(this)
      .attr('stdDeviation', '0,0');
  }

  select('#motionBlur')
    .select('feGaussianBlur')
      .transition(t)
      .attr('stdDeviation', '5,0')
      .on('end', reset);
  
  return selection.transition(t);
};

export function getTransform(obj) {
  let transform = '';
  if (obj.translateX || obj.translateY) {
    transform += `translate(${obj.translateX || 0},${obj.translateY || 0})`;
  }
  if (obj.rotate) {
    transform += `rotate(${obj.rotate})`;
  }
  if (obj.skewX) {
    transform += `skewX(${obj.skewX})`;
  }
  if (obj.scaleX || obj.scaleY) {
    transform += `scale(${obj.scaleX || 0},${obj.scaleY || 0})`;
  }
  return transform;
};

export function getPathPoints(path) {
  if (!path || !path.getTotalLength) {
    return [];
  }
  return range(path.getTotalLength()).reduce((o, l) => {
    const point = path.getPointAtLength(l);
    o[Math.round(point.x)] = point.y;
    return o;
  }, {});
}
